/**
 * BuddyGroupCard — Compact list item for the buddy groups sidebar.
 *
 * Shows group name, status badge, member count, primary indicator, and
 * a count of upcoming rodeos (with up to two rodeo names) so users can
 * see at a glance which groups are entered in upcoming events.
 * Highlights when selected in the master-detail layout.
 */

import { Users, Crown, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useBuddyGroupRodeos } from '@/hooks/useBuddyGroupRodeos';
import type { BuddyGroupSummary } from '@/types/buddy-groups';

const statusVariant: Record<string, string> = {
  active: 'bg-green-500/15 text-green-600 dark:text-green-400',
  pending: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400',
  locked: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  disbanded: 'bg-zinc-500/15 text-zinc-500',
};

interface BuddyGroupCardProps {
  group: BuddyGroupSummary;
  isSelected?: boolean;
  onClick?: () => void;
}

export function BuddyGroupCard({ group, isSelected, onClick }: BuddyGroupCardProps) {
  const displayName = group.group_name ?? 'Unnamed Group';

  // Pull this group's rodeos so we can surface an "X upcoming" hint plus
  // the first couple of rodeo names. The backend caches; React Query
  // dedupes per-group so the master-detail layout only fetches once.
  const { data: rodeos } = useBuddyGroupRodeos(group.id);
  const upcomingRodeos = (rodeos ?? []).filter((r) => r.is_upcoming);
  const upcomingCount = upcomingRodeos.length;
  const previewNames = upcomingRodeos
    .map((r) => r.name)
    .filter((n): n is string => !!n)
    .slice(0, 2);
  const overflowCount = Math.max(0, upcomingCount - previewNames.length);

  // Violet palette matches the buddy-group treatment used everywhere
  // else in the app (dashboard upcoming-rodeos, Event Detail "tied to
  // buddy group" banner, Rodeo Detail entry-point card, BuddyGroupSetup).
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg border-2 p-3 transition-colors cursor-pointer ${
        isSelected
          ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-300/60 dark:border-violet-700 dark:bg-violet-950/40 dark:ring-violet-800/60'
          : 'border-foreground/10 bg-card hover:bg-violet-50/30 dark:hover:bg-violet-950/20'
      }`}
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isSelected
            ? 'bg-violet-200 dark:bg-violet-900/60'
            : 'bg-violet-100 dark:bg-violet-950/40'
        }`}>
          <Users className="h-4 w-4 text-violet-600 dark:text-violet-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold truncate">{displayName}</span>
            {group.is_primary && (
              <Crown className="h-3 w-3 text-amber-500 shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-muted-foreground">
              {group.member_count} member{group.member_count !== 1 ? 's' : ''}
            </span>
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 h-4 ${statusVariant[group.status] ?? statusVariant.pending}`}
            >
              {group.status}
            </Badge>
          </div>
          {upcomingCount > 0 && (
            <div className="mt-1 text-[11px]">
              {/* Stack count above names so the narrow sidebar column
                  doesn't wrap "1 upcoming rodeo" onto two lines next to
                  the rodeo name. */}
              <div className="flex items-center gap-1 font-medium text-violet-700 dark:text-violet-300">
                <Calendar className="h-3 w-3 shrink-0" />
                <span>
                  {upcomingCount} upcoming rodeo{upcomingCount !== 1 ? 's' : ''}
                </span>
              </div>
              {previewNames.length > 0 && (
                <div className="mt-0.5 truncate text-muted-foreground">
                  {previewNames.join(', ')}
                  {overflowCount > 0 ? ` +${overflowCount}` : ''}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
